import { supabase } from '../lib/supabase';

export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  tags: string[];
  image_url: string;
  published_at: string | null;
  read_time_minutes: number;
  meta_title: string | null;
  meta_description: string | null;
  created_at?: string;
  updated_at?: string;
}

export type BlogPostInput = Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>;

/** Fetches every blog post (published, scheduled and drafts) for the admin. */
export async function fetchAllPostsAdmin(): Promise<BlogPost[]> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .order('published_at', { ascending: false, nullsFirst: true })
    .order('id', { ascending: false });

  if (error) throw error;
  return (data || []) as BlogPost[];
}

export async function createPost(input: BlogPostInput): Promise<BlogPost> {
  const { data, error } = await supabase
    .from('blog_posts')
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data as BlogPost;
}

export async function updatePost(id: number, input: Partial<BlogPostInput>): Promise<void> {
  const { error } = await supabase
    .from('blog_posts')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
}

export async function deletePost(id: number): Promise<void> {
  const { error } = await supabase.from('blog_posts').delete().eq('id', id);
  if (error) throw error;
}

/** Checks whether a slug is already used by another post. */
export async function isSlugTaken(slug: string, excludeId?: number): Promise<boolean> {
  let query = supabase.from('blog_posts').select('id').eq('slug', slug);
  if (excludeId != null) query = query.neq('id', excludeId);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []).length > 0;
}

/** Generates a URL-friendly slug from a title (handles Estonian characters). */
export function slugify(text: string): string {
  const map: Record<string, string> = {
    õ: 'o', ä: 'a', ö: 'o', ü: 'u', š: 's', ž: 'z',
    Õ: 'o', Ä: 'a', Ö: 'o', Ü: 'u', Š: 's', Ž: 'z',
  };
  return text
    .split('')
    .map((c) => map[c] ?? c)
    .join('')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
